import React, { useState } from 'react';
import { Clock, Users, BookOpen, Check } from 'lucide-react';
import { courses } from '../utils/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function Courses() {
  const [selectedLevel, setSelectedLevel] = useState('Tous');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  
  const levels = ['Tous', 'Débutant', 'Élémentaire', 'Intermédiaire', 'Avancé', 'Intermédiaire+'];
  const categories = ['Tous', 'Russe', 'Anglais'];
  
  let filteredCourses = courses;
  
  if (selectedCategory !== 'Tous') {
    filteredCourses = filteredCourses.filter(course => course.category === selectedCategory);
  }
  
  if (selectedLevel !== 'Tous') {
    filteredCourses = filteredCourses.filter(course => course.level === selectedLevel);
  }

  const courseBenefits = [
    "Matériel pédagogique inclus",
    "Certificat de fin de formation",
    "Accès à la bibliothèque russe",
    "Activités culturelles gratuites",
    "Support après les cours",
    "Préparation aux examens"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0039A6] to-[#007A5E] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Nos Cours de Langues</h1>
            <p className="text-xl text-white/90">
              Des programmes adaptés à tous les niveaux pour atteindre vos objectifs linguistiques
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b sticky top-[73px] z-40 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="space-y-4">
            {/* Category Filter */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2 text-center">Langue</p>
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-2 rounded-full font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-[#D52B1E] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Level Filter */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2 text-center">Niveau</p>
              <div className="flex flex-wrap gap-3 justify-center">
                {levels.map((level) => (
                  <button
                    key={level}
                    onClick={() => setSelectedLevel(level)}
                    className={`px-6 py-2 rounded-full font-medium transition-all ${
                      selectedLevel === level
                        ? 'bg-[#0039A6] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course) => (
              <Card key={course.id} className="hover:shadow-2xl transition-all transform hover:-translate-y-2 flex flex-col">
                <CardHeader>
                  <div className="flex justify-between items-start mb-3">
                    <Badge className={course.category === 'Russe' ? 'bg-[#007A5E] hover:bg-[#007A5E]' : 'bg-[#0039A6] hover:bg-[#0039A6]'}>
                      {course.level}
                    </Badge>
                    <Badge variant="outline" className="font-semibold">{course.category}</Badge>
                  </div>
                  <CardTitle className="text-2xl mb-2">{course.title}</CardTitle>
                  <p className="text-sm text-gray-500 font-medium">{course.titleRu || course.titleEn}</p>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-gray-600 mb-6 leading-relaxed">{course.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm text-gray-700">
                      <div className="w-10 h-10 bg-[#FCD116]/20 rounded-lg flex items-center justify-center">
                        <Clock size={18} className="text-[#007A5E]" />
                      </div>
                      <div>
                        <div className="font-medium">Durée</div>
                        <div className="text-gray-600">{course.duration}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-700">
                      <div className="w-10 h-10 bg-[#FCD116]/20 rounded-lg flex items-center justify-center">
                        <BookOpen size={18} className="text-[#007A5E]" />
                      </div>
                      <div>
                        <div className="font-medium">Horaires</div>
                        <div className="text-gray-600">{course.schedule}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-700">
                      <div className="w-10 h-10 bg-[#FCD116]/20 rounded-lg flex items-center justify-center">
                        <Users size={18} className="text-[#007A5E]" />
                      </div>
                      <div>
                        <div className="font-medium">Étudiants inscrits</div>
                        <div className="text-gray-600">{course.students} étudiants</div>
                      </div>
                    </div>
                  </div>
                  
                  <Button className="w-full bg-[#D52B1E] hover:bg-[#b82418] mt-auto">
                    S'inscrire maintenant
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Ce Qui Est Inclus
              </h2>
              <p className="text-lg text-gray-600">
                Tous nos cours incluent des avantages exclusifs pour votre réussite
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {courseBenefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-[#FCD116]/10 transition-colors">
                  <div className="w-8 h-8 bg-[#007A5E] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check size={18} className="text-white" />
                  </div>
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Questions Fréquentes
              </h2>
              <p className="text-lg text-gray-600">
                Trouvez les réponses aux questions les plus posées
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border">
                <AccordionTrigger className="text-left font-semibold hover:text-[#0039A6]">
                  Quel niveau choisir si je débute ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Si vous n'avez jamais étudié le russe, commencez par notre cours "Russe Débutant A1". 
                  Nous vous apprendrons l'alphabet cyrillique et les bases de la langue.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border">
                <AccordionTrigger className="text-left font-semibold hover:text-[#0039A6]">
                  Les cours sont-ils disponibles en ligne ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Oui, nous proposons également des cours en ligne via Zoom avec la même qualité 
                  d'enseignement que les cours en présentiel.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border">
                <AccordionTrigger className="text-left font-semibold hover:text-[#0039A6]">
                  Puis-je payer en plusieurs fois ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Oui, nous offrons des facilités de paiement. Vous pouvez payer mensuellement 
                  ou choisir un paiement en plusieurs tranches. Contactez-nous pour plus de détails.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border">
                <AccordionTrigger className="text-left font-semibold hover:text-[#0039A6]">
                  Offrez-vous une session d'essai gratuite ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Absolument! Nous offrons un cours d'essai gratuit pour que vous puissiez 
                  découvrir notre méthode d'enseignement avant de vous inscrire.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white rounded-lg px-6 border">
                <AccordionTrigger className="text-left font-semibold hover:text-[#0039A6]">
                  Les certificats sont-ils reconnus ?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Oui, nous préparons aux examens TORFL (ТРКИ) qui sont officiellement reconnus 
                  par le gouvernement russe et acceptés internationalement.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0039A6] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prêt à commencer votre apprentissage ?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour une consultation gratuite et choisissez le cours qui vous convient
          </p>
          <Button size="lg" className="bg-[#D52B1E] hover:bg-[#b82418] px-10 py-6 text-lg">
            Inscription Gratuite
          </Button>
        </div>
      </section>
    </div>
  );
}
